import { ApiError } from "@jobenvoy/core";
import { describeError } from "@/lib/api/client";
import type { RefusedSetting } from "@/lib/api/envoy";
import { MAX_INSTRUCTIONS, type EnvoyDraft } from "./draft";

/** The checks envoy makes, said the way envoy says them. Pure. */

/** Characters as envoy counts them: code points, not UTF-16 units. */
export function charCount(text: string): number {
    return Array.from(text.trim()).length;
}

/** The faults the form shows, by field, worded as envoy words them. */
export interface DraftErrors {
    titles?: string;
    workSettings?: string;
    instructions?: string;
    /** What envoy refused that no field here owns. */
    other?: string;
}

export function draftErrors(draft: EnvoyDraft): DraftErrors {
    const chars = charCount(draft.instructions);
    return {
        ...(draft.titles.length === 0 ? { titles: "Add at least one job title." } : {}),
        ...(draft.workSettings.length === 0
            ? { workSettings: "Pick at least one work setting." }
            : {}),
        ...(chars > MAX_INSTRUCTIONS
            ? { instructions: `Instructions are ${MAX_INSTRUCTIONS} characters at most.` }
            : {}),
    };
}

export function hasErrors(errors: DraftErrors): boolean {
    return Object.values(errors).some(Boolean);
}

/** The form's field for each fault that has one. Any other is said in the footer. */
const FIELD_OF_FAULT: Record<string, "titles" | "workSettings" | "instructions"> = {
    NO_TITLE: "titles",
    NO_WORK_SETTING: "workSettings",
    INSTRUCTIONS_TOO_LONG: "instructions",
};

/** Envoy's faults put on the form's fields, each in the words envoy gave it. */
export function refusalErrors(faults: RefusedSetting[]): DraftErrors {
    return faults.reduce<DraftErrors>((errors, { fault, message }) => {
        const field = Object.hasOwn(FIELD_OF_FAULT, fault.kind)
            ? FIELD_OF_FAULT[fault.kind]
            : "other";
        return { ...errors, [field]: errors[field] ? `${errors[field]} ${message}` : message };
    }, {});
}

/** The faults a 400 names, or null for a body that names none. */
function refusedFaults(body: string): RefusedSetting[] | null {
    try {
        const parsed = JSON.parse(body) as { faults?: unknown } | null;
        const faults = parsed?.faults;
        return Array.isArray(faults) && faults.length > 0 && faults.every(isRefusedSetting)
            ? faults
            : null;
    } catch {
        return null;
    }
}

function isRefusedSetting(fault: unknown): fault is RefusedSetting {
    const said = fault as Partial<RefusedSetting> | null;
    return typeof said?.message === "string" && typeof said.fault?.kind === "string";
}

/**
 * A failed save, on the form: a 400 naming its faults is read onto the
 * fields; anything else is said whole in the footer.
 */
export function saveErrors(error: unknown): DraftErrors {
    const faults = error instanceof ApiError && error.status === 400 && refusedFaults(error.body);
    return faults ? refusalErrors(faults) : { other: describeError(error) };
}
